import { Component, DestroyRef, effect, inject, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, ValidatorFn, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger, MatAutocomplete } from '@angular/material/autocomplete';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { faAngleRight } from '@fortawesome/free-solid-svg-icons';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { Observable, forkJoin, map, of, startWith, switchMap } from 'rxjs';
import { SdaTargetTypes } from 'src/app/models/centrex/enums/sda-target-types';
import { Extension } from 'src/app/models/centrex/extension';
import { Sda } from 'src/app/models/centrex/sda';
import { Service } from 'src/app/models/centrex/service';
import { ExtensionDisplayPipe } from 'src/app/pipes/extension-display.pipe';
import { ExtensionService } from 'src/app/services/centrex/extension.service';
import { SdaService } from 'src/app/services/centrex/sda.service';
import { ServiceService } from 'src/app/services/centrex/service.service';
import { OrganizationsService } from 'src/app/services/organizations.service';
import { ExtensionDisplayPipe as ExtensionDisplayPipe_1 } from '../../../pipes/extension-display.pipe';
import { AsyncPipe } from '@angular/common';
import { MatButton } from '@angular/material/button';
import { MatOption } from '@angular/material/core';
import { MatSelect } from '@angular/material/select';
import { MatInput } from '@angular/material/input';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatCard, MatCardContent } from '@angular/material/card';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { InstanceService } from 'src/app/services/centrex/instance.service';
import { Instance } from 'src/app/models/centrex/instance';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
    selector: 'app-sda-form',
    templateUrl: './sda-form.component.html',
    styleUrls: ['./sda-form.component.scss'],
    imports: [
        FaIconComponent,
        ReactiveFormsModule,
        MatCard,
        MatCardContent,
        MatFormField,
        MatLabel,
        MatInput,
        MatSelect,
        MatOption,
        MatAutocompleteTrigger,
        MatAutocomplete,
        MatButton,
        RouterLink,
        AsyncPipe,
        TranslateModule,
        ExtensionDisplayPipe_1,
    ]
})
export class SdaFormComponent implements OnInit {
    private readonly instanceService = inject(InstanceService);
    private readonly router = inject(Router);
    private destroyRef = inject(DestroyRef);

    // Icône
    public faAngleRight = faAngleRight;

    sdaInformations: Sda;
    public extensionsList: Extension[] = [];
    public servicesList: Service[] = [];
    sdaForm: UntypedFormGroup;
    isCreation: boolean;

    targetTypes = Object.keys(SdaTargetTypes);
    public sdaTargetTypes = SdaTargetTypes;

    extensionsOptions: Observable<Extension[]>;
    selectedExtension: Extension;
    servicesOptions: Observable<Service[]>;
    selectedService: Service;

    private previousInstance: Instance;

    constructor(
        private route: ActivatedRoute,
        private sdaService: SdaService,
        private extensionService: ExtensionService,
        private serviceService: ServiceService,
        private organizationsService: OrganizationsService,
        private translate: TranslateService,
        private toastr: ToastrService,
        private formbuilder: UntypedFormBuilder,
        private spinner: NgxSpinnerService,
        private extensionDisplayPipe: ExtensionDisplayPipe
    ) {
        this.createForm({} as Sda);

        this.previousInstance = this.instanceService.instance();

        effect(() => {
            if (this.instanceService.instance() && this.previousInstance.id !== this.instanceService.instance().id) {
                this.router.navigate(['/sdas']);
            }
        });
    }

    ngOnInit(): void {
        let sdaId: string;

        // Récupère l'identifiant de la SDA
        this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
            sdaId = params['id'];
        });

        this.loadDatas(sdaId);
    }

    loadDatas(sdaId: string) {
        this.spinner.show();

        if (sdaId) {
            this.sdaService
                .getSda(sdaId)
                .pipe(
                    switchMap((sda) => {
                        this.sdaInformations = sda;
                        this.isCreation = false;

                        return forkJoin([this.extensionService.getExtensions(), this.serviceService.getServices()]);
                    }),
                    takeUntilDestroyed(this.destroyRef)
                )
                .subscribe({
                    next: ([extensions, services]) => {
                        this.extensionsList = extensions;
                        this.servicesList = services;

                        if (this.sdaInformations.targetType === SdaTargetTypes.extension) {
                            this.selectedExtension = extensions.find((a) => a.id === this.sdaInformations.target);
                        } else if (this.sdaInformations.targetType === this.sdaTargetTypes.service) {
                            this.selectedService = services.find((a) => a.name === this.sdaInformations.target);
                        }

                        // Créé le formulaire
                        this.createForm(this.sdaInformations);
                    },
                    error: () => {
                        this.spinner.hide();
                    },
                });
        } else {
            this.isCreation = true;
            this.sdaInformations = new Sda();
            this.sdaInformations.organizationId = this.organizationsService.getCurrentOrganization()?.id ?? '';
            this.sdaInformations.targetType = SdaTargetTypes.none;

            forkJoin([this.extensionService.getExtensions(), this.serviceService.getServices()])
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe({
                    next: ([extensions, services]) => {
                        this.extensionsList = extensions;
                        this.servicesList = services;

                        // Créé le formulaire
                        this.createForm(this.sdaInformations);
                    },
                    error: () => {
                        this.spinner.hide();
                    },
                });
        }
    }

    createForm(sda: Sda) {
        this.sdaForm = this.formbuilder.group({
            id: [sda.id],
            organizationId: [sda.organizationId],
            number: [sda.number],
            targetType: [sda.targetType],
            target: [sda.target, sda.targetType !== SdaTargetTypes.none ? Validators.required : ''],
            extension: [this.selectedExtension, [this.autocompleteExtensionValidator()]],
            service: [this.selectedService, [this.autocompleteServiceValidator()]],
        });

        this.extensionsOptions = this.sdaForm.get('extension').valueChanges.pipe(
            startWith<string | Extension>(''),
            map((value) => {
                const name = typeof value === 'string' ? value : value ? this.extensionDisplayPipe.transform(value) : null;
                return name ? this.extensionSearch(name as string) : this.extensionsList.slice();
            })
        );

        this.servicesOptions = this.sdaForm.get('service').valueChanges.pipe(
            startWith<string | Service>(''),
            map((value) => {
                const name = typeof value === 'string' ? value : null;
                return name ? this.serviceSearch(name as string) : this.servicesList.slice();
            })
        );

        this.spinner.hide();
    }

    onSubmit() {
        this.spinner.show();

        this.commitChanges();

        if (this.isCreation) {
            // Appel de la méthode de création
            this.sdaService
                .addSda(this.sdaInformations)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe({
                    next: () => {
                        this.toastr.success(this.translate.instant('Sda.SuccessMessageCreateSda'));

                        // Redirige sur la page des SDA
                        this.router.navigate(['/sdas']);

                        this.spinner.hide();
                    },
                    error: () => {
                        this.toastr.error(this.translate.instant('Sda.ErrorMessageCreateSda'));
                        this.spinner.hide();
                    },
                });
        } else {
            this.sdaService
                .updateSda(this.sdaInformations)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe({
                    next: () => {
                        this.toastr.success(this.translate.instant('Sda.SuccessMessageUpdateSda'));

                        this.router.navigate(['/sdas']);

                        this.spinner.hide();
                    },
                    error: () => {
                        this.toastr.error(this.translate.instant('Sda.ErrorMessageUpdateSda'));
                        this.spinner.hide();
                    },
                });
        }
    }

    commitChanges() {
        this.sdaInformations.number = this.sdaForm.value.number;
        this.sdaInformations.targetType = this.sdaForm.value.targetType;
        this.sdaInformations.target = this.sdaForm.value.target;
    }

    displayExtension = (extension: Extension): string => {
        return extension ? this.extensionDisplayPipe.transform(extension) : '';
    };

    displayService = (service: Service): string => {
        return service ? service.name : '';
    };

    extensionsChange(event: MatAutocompleteSelectedEvent) {
        let extension = event.option.value as Extension;
        if (extension) {
            this.sdaForm.get('target').setValue(extension.id);
            this.selectedExtension = extension;
        }
    }

    servicesChange(event: MatAutocompleteSelectedEvent) {
        let service = event.option.value as Service;
        if (service) {
            this.sdaForm.get('target').setValue(service.name);
            this.selectedService = service;
        }
    }

    private extensionSearch(value: string): Extension[] {
        const filterValue = value.toLowerCase();

        return this.extensionsList.filter((a) => this.extensionDisplayPipe.transform(a).toLowerCase().includes(filterValue));
    }

    private serviceSearch(value: string): Service[] {
        const filterValue = value.toLowerCase();

        return this.servicesList.filter((a) => a.name.toLowerCase().includes(filterValue));
    }

    targetTypeChange(targetType: string) {
        this.sdaForm.get('target').setValue('');
        this.sdaForm.get('extension').setValue('');
        this.sdaForm.get('service').setValue('');
    }

    private autocompleteExtensionValidator(): ValidatorFn {
        return (control: AbstractControl): { [key: string]: boolean } | null => {
            let validation: { [key: string]: boolean } = {};

            if (control.value && this.extensionsList.findIndex((a) => a === control.value) === -1) {
                validation['invalidSelection'] = true;
                return validation;
            }

            return null;
        };
    }

    private autocompleteServiceValidator(): ValidatorFn {
        return (control: AbstractControl): { [key: string]: boolean } | null => {
            let validation: { [key: string]: boolean } = {};

            if (control.value && this.servicesList.findIndex((a) => a === control.value) === -1) {
                validation['invalidSelection'] = true;
                return validation;
            }

            return null;
        };
    }
}
